import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, useTheme } from 'styled-components';
import { orderBy } from 'natural-orderby';

import { Flex, Text, Spinner, Tooltip } from 'src/packages/core';
import { FileFilledIcon, SortUpIcon, SortDownIcon } from 'src/packages/icons';
import { isFalsy } from 'src/utils/is-falsy';

import {
  RowItemCellContent,
  THeadContentWrapper,
  THeadSortIconWrapper,
  TableContainer,
} from './Table.styles';
import { TableColumn, TableProps, SortOrderEnum } from './Table.types';
import { Pagination } from 'src/packages/core/pagination';
import { DarkTheme, LightTheme } from 'src/packages/theme';

const NoResults = ({
  defaulttheme,
  borderRadius = '8px',
}: {
  defaulttheme: DefaultTheme;
  borderRadius?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Flex
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      mt={5}
      background={defaulttheme.colors.background.secondary.BG1}
      borderRadius={borderRadius}
      gap={2}
    >
      <FileFilledIcon size={64} color={defaulttheme.colors.border.lighter} />

      <Text
        variant={'p2'}
        text={t('table.no_data_text')}
        color={defaulttheme.colors.text.tertiary}
      />
    </Flex>
  );
};

const Table: React.FC<TableProps<any>> = ({
  columns = [],
  activePage,
  totalPages,
  onPageNumClick,
  onPreviousClick,
  onNextClick,
  highlightRowsOnHover = true,
  firstColumnStickyOnMobile = false,
  lastColumnStickyOnMobile = false,
  previousDisabled = false,
  nextDisabled = false,
  hasPagination = false,
  isLoading = false,
  borderRadius = '8px',
  theme,
  rowClickHandler,
  data = [],
  ...rest
}: TableProps<any>) => {
  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'dark'
        ? { ...DarkTheme, rtl: contextTheme.rtl }
        : { ...LightTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const getAlignment = (col: TableColumn, index: number) => {
    if (col?.alignment) {
      return col?.alignment;
    }

    if (index === columns?.length - 1) {
      return 'right';
    } else {
      return 'left';
    }
  };

  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<SortOrderEnum | undefined>(undefined);

  const sortedData = useMemo(() => {
    if (!sortKey || !order) {
      return data;
    }

    // check if column has sortFunction
    const currentColumn = columns.find((c) => c.key === sortKey);

    if (
      currentColumn &&
      currentColumn.hasOwnProperty('sortFunction') &&
      typeof currentColumn.sortFunction === 'function'
    ) {
      return currentColumn.sortFunction(data, order);
    } else {
      return orderBy(data, sortKey, order);
    }
  }, [data, sortKey, order]);

  const handleSorting = (key: string | undefined) => {
    if (!key) {
      return;
    }

    setSortKey(key);

    setOrder((prevValue) => {
      if (!prevValue) {
        return SortOrderEnum.DESC;
      }

      if (prevValue === SortOrderEnum.DESC) {
        return SortOrderEnum.ASC;
      }

      setSortKey(undefined);
      return undefined;
    });
  };

  return (
    <TableContainer
      highlighted={highlightRowsOnHover}
      defaulttheme={defaultTheme}
      borderRadius={borderRadius}
      px={!isFalsy(rest.p) ? rest.p : !isFalsy(rest.px) ? rest.px : [4, 4, 8, 8]}
      py={!isFalsy(rest.p) ? rest.p : !isFalsy(rest.py) ? rest.px : [4, 4, 6, 6]}
      {...rest}
    >
      <table>
        <thead>
          <tr>
            {columns.map((col: TableColumn, index: number) => {
              const label =
                typeof col.label === 'string' ? (
                  <Text
                    variant='p3'
                    width={'100%'}
                    text={col.label ?? col.key}
                    color={defaultTheme.colors.text.tertiary}
                    textAlign={getAlignment(col, index)}
                    style={{
                      cursor: col.sortable ? 'pointer' : 'auto',
                    }}
                  />
                ) : (
                  col.label
                );

              const parseTooltip = col.tooltip ? (
                <Tooltip {...col.tooltip}>
                  <div>{label}</div>
                </Tooltip>
              ) : (
                label
              );

              return (
                <th
                  key={index}
                  className={
                    index === 0
                      ? firstColumnStickyOnMobile
                        ? 'sticky-first-cell'
                        : ''
                      : index === columns?.length - 1
                      ? lastColumnStickyOnMobile
                        ? 'sticky-last-cell'
                        : ''
                      : ''
                  }
                  style={{
                    width: col.width || 'fit-content',
                    minWidth: col.minWidth || 'unset',
                    cursor: col.sortable ? 'pointer' : 'initial',
                    textAlign: getAlignment(col, index),
                  }}
                  onClick={() => {
                    col.sortable && handleSorting(col.key);
                  }}
                >
                  <THeadContentWrapper width={'100%'}>
                    {parseTooltip}

                    {col.key === sortKey && order === 'desc' && (
                      <THeadSortIconWrapper>
                        <SortDownIcon color={defaultTheme.colors.text.tertiary} size={12} />
                      </THeadSortIconWrapper>
                    )}
                    {col.key === sortKey && order === 'asc' && (
                      <THeadSortIconWrapper>
                        <SortUpIcon color={defaultTheme.colors.text.tertiary} size={12} />
                      </THeadSortIconWrapper>
                    )}
                  </THeadContentWrapper>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {!!sortedData &&
            sortedData.length > 0 &&
            !isLoading &&
            sortedData.map((item: any, itemIndex: number) => (
              <tr
                key={itemIndex}
                className={rowClickHandler ? 'clickable' : ''}
                onClick={() => {
                  rowClickHandler && rowClickHandler(item);
                }}
              >
                {columns.map((col: TableColumn, index: number) => {
                  return (
                    <td
                      key={index}
                      className={
                        index === 0
                          ? firstColumnStickyOnMobile
                            ? 'sticky-first-cell'
                            : ''
                          : index === columns?.length - 1
                          ? lastColumnStickyOnMobile
                            ? 'sticky-last-cell'
                            : ''
                          : ''
                      }
                    >
                      <RowItemCellContent alignment={getAlignment(col, index)}>
                        {col.hasOwnProperty('cellRenderer') &&
                        typeof col.cellRenderer === 'function' ? (
                          col.cellRenderer(item)
                        ) : (
                          <Text
                            variant='p2'
                            width={'100%'}
                            color={defaultTheme.colors.text.primary}
                            textAlign={getAlignment(col, index)}
                            text={item[col.key ?? '']}
                          />
                        )}
                      </RowItemCellContent>
                    </td>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </table>

      {isLoading && (
        <Spinner
          inline={true}
          size={48}
          style={{ marginTop: 24 }}
          color={defaultTheme.colors.text.primary}
        />
      )}

      {!isLoading && hasPagination && data && data.length > 0 && (
        <Pagination
          theme={theme}
          activePage={activePage}
          totalPages={totalPages}
          nextDisabled={nextDisabled}
          previousDisabled={previousDisabled}
          onPageNumClick={onPageNumClick}
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
        />
      )}

      {(!data || (data && data.length === 0)) && !isLoading ? (
        <NoResults defaulttheme={defaultTheme} borderRadius={borderRadius} />
      ) : null}
    </TableContainer>
  );
};

export default Table;
